/**
 * Tienda de extensiones (Owner) — publicar/ocultar/versionar/retirar + SDK + analytics.
 * Tenant never sees this.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Package } from "lucide-react";
import { cn } from "@/lib/utils";
import { apiFetch, clientRequestHeaders, ensureCsrfToken } from "@/lib/sm-api";

export const Route = createFileRoute("/admin/ops/ai/marketplace")({
  component: OwnerMarketplacePage,
  head: () => ({ meta: [{ title: "Tienda de extensiones — Owner" }] }),
});

type Tab = "catalog" | "deps" | "sdk" | "analytics" | "health";

interface ExtRow {
  id: string;
  name: string;
  description: string;
  version: string;
  type: string;
  catalog_status: string;
  category: string;
}

function OwnerMarketplacePage() {
  const [tab, setTab] = useState<Tab>("catalog");
  const [catalog, setCatalog] = useState<ExtRow[]>([]);
  const [extra, setExtra] = useState<Record<string, unknown> | null>(null);
  const [busy, setBusy] = useState(false);

  async function load(view = "marketplace") {
    const res = await fetch(`/api/admin/ai/extensions?view=${view}`, { credentials: "include" });
    const data = await res.json() as Record<string, unknown>;
    if (view === "marketplace" && Array.isArray(data.catalog)) {
      setCatalog(data.catalog as ExtRow[]);
    }
    setExtra(data);
  }

  useEffect(() => {
    void load("marketplace");
  }, []);

  async function post(action: string, extensionId?: string, body: Record<string, unknown> = {}) {
    setBusy(true);
    try {
      const res = await apiFetch("/api/admin/ai/extensions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action, extensionId, ...body }),
      });
      const data = await res.json() as { error?: string; message?: string };
      if (!res.ok) {
        toast.error(data.error ?? "Error");
        return;
      }
      if (data.message) toast.success(data.message);
      await load("marketplace");
    } finally {
      setBusy(false);
    }
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: "catalog", label: "Extensiones" },
    { id: "deps", label: "Dependencias" },
    { id: "sdk", label: "SDK" },
    { id: "analytics", label: "Analytics" },
    { id: "health", label: "Health" },
  ];

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div>
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <Package className="h-5 w-5 text-violet-600" /> Tienda de extensiones
        </h1>
        <p className="text-sm text-muted-foreground">
          Publicar, ocultar, versionar y retirar extensiones declarativas. Sin plugins de código arbitrario.
        </p>
      </div>

      <nav className="flex flex-wrap gap-1 border-b pb-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => {
              setTab(t.id);
              if (t.id === "deps") void load("deps");
              if (t.id === "sdk") void load("sdk");
              if (t.id === "analytics") void load("analytics");
              if (t.id === "health") void load("health");
              if (t.id === "catalog") void load("marketplace");
            }}
            className={cn(
              "rounded-md px-2.5 py-1.5 text-xs",
              tab === t.id
                ? "bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-200"
                : "text-muted-foreground",
            )}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {tab === "catalog" && (
        <div className="space-y-2">
          {catalog.map((e) => (
            <div key={e.id} className="rounded-xl border p-3 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">{e.name}</span>
                <Badge variant="outline">{e.type}</Badge>
                <Badge>{e.catalog_status}</Badge>
                <Badge variant="secondary">v{e.version}</Badge>
              </div>
              <p className="text-xs text-muted-foreground">{e.description}</p>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" disabled={busy} onClick={() => void post("publish", e.id)}>Publicar</Button>
                <Button size="sm" variant="outline" disabled={busy} onClick={() => void post("hide", e.id)}>Ocultar</Button>
                <Button size="sm" variant="outline" disabled={busy} onClick={() => void post("version", e.id)}>Versionar</Button>
                <Button size="sm" variant="outline" disabled={busy} onClick={() => void post("sandbox", e.id)}>Sandbox</Button>
                <Button size="sm" variant="destructive" disabled={busy} onClick={() => void post("retire", e.id)}>Retirar</Button>
              </div>
            </div>
          ))}
          {!catalog.length ? (
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando catálogo…
            </p>
          ) : null}
        </div>
      )}

      {tab !== "catalog" && extra && (
        <pre className="max-h-[520px] overflow-auto rounded-md bg-muted/40 p-3 text-xs whitespace-pre-wrap">
          {JSON.stringify(extra, null, 2)}
        </pre>
      )}
    </div>
  );
}
